import fetchImage from './fetch-image';

const MAX_THUMBNAIL_DIMENSION = 600;

const resizeImageToFitThumbnail = (image, callback) => {
  let w;
  let h;
  if (image.width > image.height) {
    w = MAX_THUMBNAIL_DIMENSION;
    h = (MAX_THUMBNAIL_DIMENSION * image.height) / image.width;
  } else {
    h = MAX_THUMBNAIL_DIMENSION;
    w = (MAX_THUMBNAIL_DIMENSION * image.width) / image.height;
  }

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.scale(w / image.width, w / image.width);
  ctx.drawImage(image, 0, 0);
  if (typeof canvas.toBlob === 'function') {
    canvas.toBlob((blob) => {
      const url = (window.URL || window.webkitURL).createObjectURL(blob);
      callback(url);
    }, 'image/jpeg');
  } else {
    callback(canvas.toDataURL('image/jpeg'));
  }
};

/**
 * Preloads an image, attempting to fetch it from a series of fallback URLs
 * (thumb_url, large_url, raw_url). If a fetched image is larger than
 * MAX_THUMBNAIL_DIMENSION in either width or height, it is resized using
 * resizeImageToFitThumbnail to fit within the specified dimensions while
 * maintaining aspect ratio.
 *
 * @param {object} photo - The photo object containing image URLs.
 * @param {string} photo.thumb_url - The URL for the thumbnail.
 * @param {string} photo.large_url - The URL for a larger version of the image.
 * @param {string} photo.raw_url - The URL for the raw, original image.
 * @returns {Promise<string>} A promise that resolves with the URL of the
 *   (potentially resized) image, or rejects if all URLs fail to load.
 */
const preloadImage = (photo) =>
  new Promise((resolve, reject) => {
    let isSettled = false;
    const urls = [photo.thumb_url, photo.large_url, photo.raw_url].filter(
      Boolean,
    );

    const tryUrl = (index) => {
      if (index >= urls.length) {
        if (!isSettled) {
          isSettled = true;
          reject(new Error('All image URLs failed to load.'));
        }
        return;
      }

      fetchImage(
        urls[index],
        '',
        function () {
          if (isSettled) return;
          isSettled = true;

          // `this` is the image object from fetchImage
          if (
            this.width > MAX_THUMBNAIL_DIMENSION ||
            this.height > MAX_THUMBNAIL_DIMENSION
          ) {
            resizeImageToFitThumbnail(this, (resizedUrl) => {
              resolve(resizedUrl);
            });
          } else {
            resolve(this.src);
          }
        },
        () => {
          // Try the next URL
          tryUrl(index + 1);
        },
      );
    };

    // Start by trying the first URL, `thumb_url`
    tryUrl(0);
  });

export default preloadImage;
